/**
 * ClapButton — Medium-style tap-and-hold reaction. Every tap (or every
 * 80ms of a long-press) increments the local count by 1 up to 50 per
 * reader per article. New claps are batched into one server request
 * so mashing the button 30 times doesn't fire 30 HTTP calls.
 *
 * Debounce window: 700ms of no new taps → flush the pending delta.
 * The optimistic total updates instantly; the server response
 * reconciles the truth (in case another reader clapped too).
 *
 * Backend contract:
 *   GET  /articles/{slug}/claps            → {total_claps, unique_clappers, my_claps}
 *   POST /articles/{slug}/clap  {count: N} → same shape
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Hand } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
const MAX = 50;
const FLUSH_MS = 700;
const HOLD_TICK_MS = 90;

function token() {
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token") || "";
}

async function api(path, opts = {}) {
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export default function ClapButton({
  slug,
  initialTotal = 0,
  size = "md",
  testIdPrefix = "clap-button",
}) {
  const [total, setTotal] = useState(initialTotal);
  const [myClaps, setMyClaps] = useState(0);
  const [pending, setPending] = useState(0);
  const [burst, setBurst] = useState(0);   // triggers the +N floating animation
  const flushTimer = useRef(null);
  const holdTimer = useRef(null);
  const pendingRef = useRef(0);

  // Hydrate the reader's current clap count on mount.
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    (async () => {
      try {
        const d = await api(`/articles/${slug}/claps`);
        if (!alive) return;
        setTotal(d.total_claps || 0);
        setMyClaps(d.my_claps || 0);
      } catch { /* offline is fine */ }
    })();
    return () => { alive = false; };
  }, [slug]);

  const flush = useCallback(async () => {
    const delta = pendingRef.current;
    if (delta <= 0) return;
    pendingRef.current = 0;
    setPending(0);
    try {
      const d = await api(`/articles/${slug}/clap`, {
        method: "POST",
        body: JSON.stringify({ count: delta }),
      });
      setTotal(d.total_claps);
      setMyClaps(d.my_claps);
    } catch {
      // Roll back the optimistic bump on failure.
      setTotal((t) => Math.max(0, t - delta));
    }
  }, [slug]);

  const tick = useCallback(() => {
    if (myClaps + pendingRef.current >= MAX) return;
    pendingRef.current += 1;
    setPending((p) => p + 1);
    setTotal((t) => t + 1);
    setBurst((b) => b + 1);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, FLUSH_MS);
  }, [myClaps, flush]);

  const onPointerDown = (e) => {
    e.preventDefault();
    tick();
    if (holdTimer.current) clearInterval(holdTimer.current);
    holdTimer.current = setInterval(tick, HOLD_TICK_MS);
  };
  const stopHold = () => {
    if (holdTimer.current) {
      clearInterval(holdTimer.current);
      holdTimer.current = null;
    }
  };

  useEffect(() => () => {
    if (flushTimer.current) clearTimeout(flushTimer.current);
    if (holdTimer.current) clearInterval(holdTimer.current);
    // Fire the flush synchronously on unmount so we don't lose taps.
    if (pendingRef.current > 0) flush();
  }, [flush]);

  const filled = myClaps + pending > 0;
  const capped = myClaps + pending >= MAX;
  const totalMe = myClaps + pending;

  const sizes = {
    sm: { pill: "px-2.5 py-1 text-xs",   icon: "w-3.5 h-3.5" },
    md: { pill: "px-3.5 py-1.5 text-sm", icon: "w-4 h-4" },
    lg: { pill: "px-5 py-2.5 text-base", icon: "w-5 h-5" },
  }[size] || { pill: "px-3.5 py-1.5 text-sm", icon: "w-4 h-4" };

  return (
    <div className="inline-flex items-center gap-2 select-none" data-testid={testIdPrefix}>
      <div className="relative">
        <button
          type="button"
          onPointerDown={onPointerDown}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          className={`inline-flex items-center gap-1.5 rounded-full font-semibold transition-transform active:scale-95 ${sizes.pill} ${
            filled
              ? "bg-teal-700 text-white shadow-sm hover:bg-teal-800"
              : "bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
          } ${capped ? "opacity-90 cursor-not-allowed" : "cursor-pointer"}`}
          disabled={capped}
          title={capped ? "You've maxed out at 50 claps for this story." : "Tap or hold to clap (up to 50)"}
          data-testid={`${testIdPrefix}-tap`}
        >
          <Hand className={`${sizes.icon} ${filled ? "" : "text-teal-700"}`} />
          <span data-testid={`${testIdPrefix}-total`}>{total}</span>
          {totalMe > 0 && (
            <span
              className={`ml-1 rounded-full px-1.5 text-[10px] font-bold ${
                filled ? "bg-white/25 text-white" : "bg-teal-100 text-teal-800"
              }`}
              data-testid={`${testIdPrefix}-mine`}
            >
              +{totalMe}
            </span>
          )}
        </button>
        {/* Floating +1 burst on every tap — CSS transform, no lib. */}
        {burst > 0 && (
          <span
            key={burst}
            className="pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 text-teal-700 text-sm font-bold clap-burst"
            aria-hidden
          >
            +1
          </span>
        )}
      </div>
      {capped && (
        <span className="text-[11px] text-slate-500 italic" data-testid={`${testIdPrefix}-capped`}>
          Max reached
        </span>
      )}
      <style>{`
        @keyframes clapBurst {
          0%   { opacity: 0; transform: translate(-50%, 0)     scale(0.6); }
          20%  { opacity: 1; transform: translate(-50%, -12px) scale(1);   }
          100% { opacity: 0; transform: translate(-50%, -32px) scale(0.8); }
        }
        .clap-burst { animation: clapBurst 700ms ease-out forwards; }
      `}</style>
    </div>
  );
}
